import MapboxLanguage from '@mapbox/mapbox-gl-language';
import { I18n } from '@protector/utils';
import { toLower } from 'lodash';
import type { Map } from 'mapbox-gl';
import { useCallback, useEffect, useMemo } from 'react';
import { MapboxStyle } from '../core-map.constants';

interface UseMapLanguage {
  map?: Map;
  style: MapboxStyle;
}

const getLanguageWithoutLocale = (language: string) => language.split('-')[0];

export const useMapLanguage = ({ map, style }: UseMapLanguage) => {
  const interfaceLanguage = I18n.getCurrentLanguage();

  const languageControl = useMemo(
    () => new MapboxLanguage({ defaultLanguage: getLanguageWithoutLocale(interfaceLanguage) }),
    [interfaceLanguage]
  );

  const acceptedLanguages = useMemo(
    () => new Set((languageControl as MapboxLanguage & { supportedLanguages: string[] }).supportedLanguages.map(toLower)),
    [languageControl]
  );

  const parseLanguageToSupportedLanguage = useCallback(
    (language: string) => {
      const lowered = language.toLocaleLowerCase();

      if (acceptedLanguages.has(lowered)) {
        return lowered;
      }

      const languageWithoutLocale = getLanguageWithoutLocale(lowered);

      if (acceptedLanguages.has(languageWithoutLocale)) {
        return languageWithoutLocale;
      }

      const fallback = 'en';

      if (acceptedLanguages.has(fallback)) {
        return fallback;
      }

      return Array.from(acceptedLanguages)[0];
    },
    [acceptedLanguages]
  );

  const applyLanguage = useCallback(() => {
    if (style === MapboxStyle.SATELLITE) return;

    if (!interfaceLanguage || !map?.isStyleLoaded()) return;

    const parsedLanguage = parseLanguageToSupportedLanguage(interfaceLanguage);

    map.setStyle(languageControl.setLanguage(map.getStyle(), parsedLanguage));
  }, [interfaceLanguage, map, style, parseLanguageToSupportedLanguage, languageControl]);

  useEffect(() => {
    applyLanguage();
  }, [interfaceLanguage, applyLanguage]);

  return {
    languageControl
  };
};
